/**
 * Zyklische Stützstellenkurven — die Grundrechenart des Referenzgangs.
 *
 * Die Normkurven des Gangs liegen als Stützstellen `[Phase in %, Wert]` vor und
 * werden dazwischen kubisch interpoliert. Zyklisch heisst: 100 % ist wieder 0 %,
 * und die Tangenten am Rand kommen von der jeweils anderen Seite des Zyklus.
 * Ohne diesen Umlauf hätte jede Kurve am Zyklusanfang einen Knick — und ein
 * Knick in der Winkelkurve wird in der zweiten Ableitung zu einem Ausschlag,
 * der als Beschleunigungsspitze durch die gesamte inverse Dynamik läuft.
 *
 * Übernommen aus `evalKF` der Vorlage, Verhalten unverändert.
 */

/** Eine Stützstelle: Phase in Prozent des Doppelschritts, Wert in Grad. */
export type Keyframe = readonly [phasePercent: number, value: number]

/** Eine Kurve über den Zyklus. Stützstellen aufsteigend nach Phase. */
export type Curve = readonly Keyframe[]

export const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v))

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/**
 * Wert der Kurve an der Stelle `x` (Phase in Prozent, beliebig gross oder
 * negativ — es wird umlaufend gerechnet).
 *
 * Kubische Hermite-Interpolation. Die Tangente an einer Stützstelle ergibt sich
 * aus ihren beiden Nachbarn; ausserhalb des Feldes werden die Nachbarn um einen
 * vollen Zyklus versetzt herangezogen. Das ist der Grund für die
 * Fallunterscheidungen unten und nicht etwa Vorsicht gegen leere Felder.
 */
export function evalCurve(curve: Curve, x: number): number {
  const phase = (((x % 100) + 100) % 100)
  const n = curve.length
  if (n === 0) return 0
  if (n === 1) return curve[0][1]

  let i = 0
  while (i < n - 1 && curve[i + 1][0] <= phase) i++

  const p1 = curve[i]
  const p2: Keyframe = i + 1 < n ? curve[i + 1] : [curve[0][0] + 100, curve[0][1]]
  const p0: Keyframe = i - 1 >= 0 ? curve[i - 1] : [curve[n - 1][0] - 100, curve[n - 1][1]]
  const p3: Keyframe =
    i + 2 < n
      ? curve[i + 2]
      : i + 2 === n
        ? [curve[0][0] + 100, curve[0][1]]
        : [curve[1][0] + 100, curve[1][1]]

  const h = p2[0] - p1[0]
  const s = h > 0 ? (phase - p1[0]) / h : 0
  const m1 = ((p2[1] - p0[1]) / (p2[0] - p0[0])) * h
  const m2 = ((p3[1] - p1[1]) / (p3[0] - p1[0])) * h

  const s2 = s * s
  const s3 = s2 * s
  return (
    (2 * s3 - 3 * s2 + 1) * p1[1] +
    (s3 - 2 * s2 + s) * m1 +
    (-2 * s3 + 3 * s2) * p2[1] +
    (s3 - s2) * m2
  )
}

/**
 * Mittelwert der Kurve über den Zyklus.
 *
 * Bewusst als Mittel über 100 gleichabständige Stellen und nicht als Integral
 * über die Stützstellen: die Vorlage rechnet so, und der Wert geht in die
 * Ruhelage einzelner Gelenke ein. Ein „genaueres" Integral würde hier still
 * andere Ergebnisse liefern als das Original.
 */
export function meanCurve(curve: Curve): number {
  let sum = 0
  for (let i = 0; i < 100; i++) sum += evalCurve(curve, i)
  return sum / 100
}
